import { useState, type FormEvent } from "react";
import logo from "../../../../logo.png";
import logoWithWords from "../../../../logo-with-words.png";

type Status = "idle" | "loading" | "success" | "error";

const formatPhoneNumber = (val: string) => {
  const digits = val.replace(/\D/g, "");
  if (digits.length <= 3) return digits.length > 0 ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState<string>("");
  const [phone, setPhone] = useState<string>("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    // Honeypot check
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      setPhone("");
      formEl.reset();
      return;
    }
    formData.delete("website_alt");

    // Character limit on textarea
    const message = String(formData.get("message") || "");
    if (message.length > 500) {
      setStatus("error");
      setFormError("Message must be 500 characters or fewer.");
      return;
    }

    setStatus("loading");
    setFormError("");

    try {
      const data: Record<string, string> = {};
      formData.forEach((value, key) => {
        data[key] = String(value);
      });

      // Map fields for Formspree
      data["_replyto"] = data["email"] ?? "";
      data["_subject"] = `New Compliance Audit Request – ${data["association"] ?? "Association Integrity"}`;

      const response = await fetch("https://formspree.io/f/xzepjdyo", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(data),
      });

      const parsed: { ok?: boolean; errors?: { field: string; message: string }[] } =
        await response.json().catch(() => ({}));

      if (response.ok && parsed?.ok) {
        setStatus("success");
        setPhone("");
        formEl.reset();
      } else {
        const errMsg = parsed?.errors?.map((e) => e.message).join(" ") || "";
        setStatus("error");
        setFormError(
          errMsg || "We couldn't submit your request. Please try again or call (954) 444-4747."
        );
      }
    } catch {
      setStatus("error");
      setFormError("Network error. Please try again or call (954) 444-4747.");
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="rounded-lg overflow-hidden border border-background-200 grid lg:grid-cols-5 bg-background-100">
          <div className="lg:col-span-2 bg-foreground-950 text-background-50 p-10 md:p-14 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "url('https://readdy.ai/api/search-image?query=abstract%20soft%20blurred%20south%20florida%20palm%20trees%20at%20sunset%20with%20warm%20amber%20golden%20gradient%20sky%2C%20minimal%20editorial%20photography%20with%20soft%20focus%20and%20cinematic%20mood%2C%20quiet%20contemplative%20tropical%20atmosphere%20with%20negative%20space&width=1200&height=1600&seq=contact-bg-05&orientation=portrait')",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div className="relative z-10">
              {/* Logo with words */}
              <div className="mb-8 flex justify-center">
                <img
                  src={logoWithWords}
                  alt="Association Integrity – Compliant Websites Stronger Communities Greater Trust"
                  className="h-32 w-auto object-contain"
                />
              </div>

              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-accent-300 font-semibold">
                <i className="ri-calendar-check-line" />
                Free 30-min consult
              </span>
              <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-background-50 leading-[1.05]">
                Book your compliance conversation.
              </h2>
              <p className="mt-4 text-background-100 leading-relaxed">
                Tell us a little about your community. Within one business day, a senior consultant will
                schedule a call, walk your current website, and send a written scope you can share with
                the board.
              </p>

              <div className="mt-10 space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-background-50/10 border border-background-50/20">
                    <i className="ri-phone-line" />
                  </div>
                  <div>
                    <div className="text-xs text-background-100 uppercase tracking-wide">Phone</div>
                    <a
                      href="tel:+19544444747"
                      className="font-heading text-lg font-semibold cursor-pointer"
                    >
                      (954) 444-4747
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-background-50/10 border border-background-50/20">
                    <i className="ri-mail-line" />
                  </div>
                  <div>
                    <div className="text-xs text-background-100 uppercase tracking-wide">Email</div>
                    <a
                      href="mailto:boards@associationintegrity.com"
                      className="font-heading text-base font-semibold cursor-pointer break-all"
                    >
                      boards@associationintegrity.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-background-50/10 border border-background-50/20">
                    <i className="ri-map-pin-line" />
                  </div>
                  <div>
                    <div className="text-xs text-background-100 uppercase tracking-wide">Location</div>
                    <div className="font-heading text-base font-semibold">
                      Fort Lauderdale, FL
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 p-10 md:p-14">
            {status === "success" ? (
              <div className="h-full flex flex-col items-start justify-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-6">
                  <i className="ri-check-double-line text-2xl" />
                </div>
                <h3 className="font-heading text-3xl font-semibold text-foreground-950">
                  Thank you, we've received your request.
                </h3>
                <p className="mt-3 text-foreground-700 leading-relaxed">
                  A senior consultant will reach out within one business day. For anything urgent,
                  call us directly at (954) 444-4747.
                </p>
                <button
                  onClick={() => {
                    setStatus("idle");
                    setFormError("");
                    setPhone("");
                  }}
                  className="mt-6 text-primary-700 hover:text-primary-800 font-semibold cursor-pointer whitespace-nowrap"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form
                id="association-integrity-contact"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      First name
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      required
                      className="w-full text-sm bg-background-50 border border-background-200 rounded-md px-4 py-3 text-foreground-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      Last name
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      required
                      className="w-full text-sm bg-background-50 border border-background-200 rounded-md px-4 py-3 text-foreground-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full text-sm bg-background-50 border border-background-200 rounded-md px-4 py-3 text-foreground-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      Phone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                      maxLength={14}
                      placeholder="(954) 444-4747"
                      className="w-full text-sm bg-background-50 border border-background-200 rounded-md px-4 py-3 text-foreground-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="association" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Association or community name
                  </label>
                  <input
                    id="association"
                    name="association"
                    required
                    className="w-full text-sm bg-background-50 border border-background-200 rounded-md px-4 py-3 text-foreground-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="communityType" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      Community type
                    </label>
                    <select
                      id="communityType"
                      name="communityType"
                      required
                      defaultValue=""
                      className="w-full text-sm bg-background-50 border border-background-200 rounded-md px-4 py-3 text-foreground-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 cursor-pointer"
                    >
                      <option value="" disabled>
                        Select one…
                      </option>
                      <option>HOA (homeowner association)</option>
                      <option>Condominium association</option>
                      <option>Cooperative</option>
                      <option>55+ / age-restricted (HOPA)</option>
                      <option>Master / mixed-use community</option>
                      <option>Property management company</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="unitCount" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      Approximate units / parcels
                    </label>
                    <select
                      id="unitCount"
                      name="unitCount"
                      required
                      defaultValue=""
                      className="w-full text-sm bg-background-50 border border-background-200 rounded-md px-4 py-3 text-foreground-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 cursor-pointer"
                    >
                      <option value="" disabled>
                        Select one…
                      </option>
                      <option>Under 100</option>
                      <option>100 – 250</option>
                      <option>250 – 500</option>
                      <option>500 – 1,000</option>
                      <option>1,000+</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    What's on your mind?
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="A demand letter, upcoming audit, board request, or just a general check-up…"
                    className="w-full text-sm bg-background-50 border border-background-200 rounded-md px-4 py-3 text-foreground-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 resize-none"
                  />
                  <div className="text-xs text-foreground-500 mt-1">Max 500 characters.</div>
                </div>

                {/* Honeypot */}
                <div className="ai-field-alt" aria-hidden="true">
                  <label htmlFor="website_alt">Website (leave blank)</label>
                  <input
                    id="website_alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === "error" && formError && (
                  <div className="text-sm text-accent-800 bg-accent-100 border border-accent-200 rounded-md px-4 py-3">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 disabled:opacity-70 text-background-50 font-semibold px-8 py-4 rounded-md flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-colors"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Request my free audit
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>
                <p className="text-xs text-foreground-500">
                  By submitting, you agree to be contacted about your community's compliance
                  needs. We'll never share your information.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}